import {
	Box,
	Button,
	Flex,
	Heading,
	BackIcon,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalFooter,
	ModalHeader,
	Stack,
	Text,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useLocalStorage } from "usehooks-ts";
import { SplashScreen } from "./components/splash-screen";
import { getOnboardingImages } from "./utils/onboarding-images";
import { type JSX, useState } from "react";
import { MiniStepper } from "./components/mini-stepper";
import { SwipeGesture } from "./components/swipe-gesture";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { useAppContext } from "./hooks/use-app-context";

type props = {
	children: JSX.Element;
};

export const PreLoginOnboarding = ({ children }: props) => {
	const { t, i18n } = useTranslation();
	const { safeArea } = useAppContext();
	const [onboardingComplete, setOnboardingComplete] = useLocalStorage("pre-login-onboarding-complete", false);
	const [stepperCount, setStepperCount] = useState(1);

	const images = getOnboardingImages(i18n.language);

	const stepContent: Content[] = [
		{
			imageUrl: images.schedule,
			title: t("pre_login_onboarding_welcome_title"),
			description: t("pre_login_onboarding_welcome_description"),
		},
		{
			imageUrl: images.tradeShift,
			title: t("pre_login_onboarding_trade_shift_title"),
			description: t("pre_login_onboarding_trade_shift_description"),
		},
		{
			imageUrl: images.tip,
			title: t("pre_login_onboarding_tip_title"),
			description: t("pre_login_onboarding_tip_description"),
		},
		{
			imageUrl: images.news,
			title: t("pre_login_onboarding_message_title"),
			description: t("pre_login_onboarding_message_description"),
		},
		{
			imageUrl: images.messages,
			title: t("pre_login_onboarding_multiple_employees_title"),
			description: t("pre_login_onboarding_multiple_employees_description"),
		},
	];

	const onClose = () => {
		setOnboardingComplete(true);
	};

	const onSwipeLeft = () => {
		if (stepperCount < stepContent.length) {
			setStepperCount(stepperCount + 1);
		}
	};

	const onSwipeRight = () => {
		if (stepperCount !== 1 && stepperCount > 0) {
			setStepperCount(stepperCount - 1);
		}
	};

	if (!onboardingComplete) {
		return (
			<>
				<link rel="preload" href={images.schedule} as="image" type="image/avif" />
				<link rel="preload" href={images.tradeShift} as="image" type="image/avif" />
				<link rel="preload" href={images.tip} as="image" type="image/avif" />
				<link rel="preload" href={images.messages} as="image" type="image/avif" />
				<SplashScreen />;
				<SwipeGesture swipeLeft={onSwipeLeft} swipeRight={onSwipeRight}>
					<Modal
						isOpen={!onboardingComplete}
						onClose={onClose}
						closeOnOverlayClick={false}
						size={isMobile ? "full" : undefined}
					>
						<ModalHeader data-testid="onboarding-modal-header">
							<Flex
								m={ThemeSpaceVariable.Medium}
								mt={safeArea.topAsPx(ThemeSpaceVariable.Medium)}
								display={"flex"}
								justifyContent={"space-between"}
							>
								{stepperCount > 1 ? (
									<Button
										leftIcon={BackIcon}
										variant={"ghost"}
										onClick={() => setStepperCount(stepperCount - 1)}
										data-testid="onboarding-modal-header-back-button"
									>
										{t("choice_back")}
									</Button>
								) : (
									<div></div>
								)}
								<ModalCloseButton />
							</Flex>
						</ModalHeader>
						<ModalBody data-testid="onboarding-modal-body">
							<>
								<Image src={stepContent[stepperCount - 1].imageUrl} width={"min(65%, 30vh)"} margin="auto" />
								<Stack
									gap={ThemeSpaceVariable.Medium}
									m={ThemeSpaceVariable.Large}
									mt={ThemeSpaceVariable.Medium}
									mb={ThemeSpaceVariable.Medium}
									textAlign={"center"}
								>
									<Heading as="h1">{stepContent[stepperCount - 1].title}</Heading>
									<Text data-testid="onboarding-description">
										{stepContent[stepperCount - 1].description}
									</Text>
								</Stack>
							</>
						</ModalBody>
						<ModalFooter paddingBottom={safeArea.bottomAsPx()} data-testid="onboarding-modal-footer">
							<Stack
								p={ThemeSpaceVariable.Medium}
								pb={ThemeSpaceVariable.Large}
								w={"100%"}
								gap={ThemeSpaceVariable.Medium}
							>
								<Box m={"auto"} w={"fit-content"}>
									<MiniStepper
										activeStep={stepperCount}
										totalSteps={stepContent.length}
										stepCountChange={setStepperCount}
									/>
								</Box>
								{stepperCount < stepContent.length ? (
									<Button
										w={"100%"}
										onClick={() => setStepperCount(stepperCount + 1)}
										variant={"primary"}
										size={"lg"}
										data-testid="onboarding-button"
									>
										{t("choice_next")}
									</Button>
								) : (
									<Button
										w={"100%"}
										onClick={onClose}
										variant={"primary"}
										size={"lg"}
										data-testid="onboarding-button"
									>
										{t("choice_done")}
									</Button>
								)}
							</Stack>
						</ModalFooter>
					</Modal>
				</SwipeGesture>
			</>
		);
	} else {
		return children;
	}
};

type Content = {
	imageUrl: string;
	title: string;
	description: string;
};
